import { SystemPermissionResponse } from "../services/systemPermissionService";

export interface GroupedPermissions {
    [category: string]: SystemPermissionResponse[];
}

/**
 * Groups permissions by their category prefix (e.g., TRANSACTION:, REPORT:)
 * @param permissions - Array of permissions to group
 * @returns Object with categories as keys and permission arrays as values
 */
export const groupPermissionsByCategory = (permissions: SystemPermissionResponse[]): GroupedPermissions => {
    return permissions.reduce((groups: GroupedPermissions, permission) => {
        // Extract category from permission name (e.g., "TRANSACTION" from "TRANSACTION:RECEIPT")
        const category = permission.name?.split(':')?.[0] || 'OTHER';

        if (!groups[category]) {
            groups[category] = [];
        }

        groups[category].push(permission);
        return groups;
    }, {});
};

/**
 * Sort categories in a logical order
 * @param categories - Array of category names
 * @returns Sorted array of category names
 */
export const sortCategories = (categories: string[]): string[] => {
    const categoryOrder = ['TRANSACTION', 'REPORT', 'USER', 'ACCOUNT', 'BRANCH', 'SYSTEM'];

    return categories.sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a);
        const bIndex = categoryOrder.indexOf(b);

        // If both are in the predefined order, sort by order
        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        // If one is in predefined order, it comes first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // Otherwise sort alphabetically
        return a.localeCompare(b);
    });
};

/**
 * Check if all permissions in a category are selected
 * @param categoryPermissions - Permissions in the category
 * @param selectedPermissions - Set of selected permission IDs
 * @returns true if all permissions in category are selected
 */
export const isCategoryFullySelected = (
    categoryPermissions: SystemPermissionResponse[],
    selectedPermissions: Set<number>
): boolean => {
    return categoryPermissions.every(perm => selectedPermissions.has(perm.id || 0));
};

/**
 * Check if any permissions in a category are selected
 * @param categoryPermissions - Permissions in the category
 * @param selectedPermissions - Set of selected permission IDs
 * @returns true if any permissions in category are selected
 */
export const isCategoryPartiallySelected = (
    categoryPermissions: SystemPermissionResponse[],
    selectedPermissions: Set<number>
): boolean => {
    return categoryPermissions.some(perm => selectedPermissions.has(perm.id || 0));
};

/**
 * Get permission IDs for a category
 * @param categoryPermissions - Permissions in the category
 * @returns Array of permission IDs in the category
 */
export const getCategoryPermissionIds = (categoryPermissions: SystemPermissionResponse[]): number[] => {
    return categoryPermissions.map(perm => perm.id || 0).filter(id => id !== 0);
};
